import { faMoon, Icon } from "@rivet-gg/icons";
import { useQuery } from "@tanstack/react-query";
import type { ComponentPropsWithRef } from "react";
import { cn, Ping } from "@/components";
import { useDataProvider } from "./data-provider";
import type { ActorId, ActorStatus } from "./queries";

export const QueriedActorStatusIndicator = ({
	actorId,
	...props
}: {
	showOnlyFatal?: boolean;
	actorId: ActorId;
} & ComponentPropsWithRef<"span">) => {
	const { data: status = "unknown", isError } = useQuery(
		useDataProvider().actorStatusQueryOptions(actorId),
	);

	return (
		<ActorStatusIndicator
			status={isError ? "stopped" : status}
			{...props}
		/>
	);
};

interface ActorStatusIndicatorProps extends ComponentPropsWithRef<"span"> {
	status: ActorStatus | undefined;
	showOnlyFatal?: boolean;
}

export const ActorStatusIndicator = ({
	status,
	showOnlyFatal = false,
	...props
}: ActorStatusIndicatorProps) => {
	if (showOnlyFatal && status !== "crashed" && status !== "crash-loop") {
		return null;
	}
	if (status === "sleeping") {
		return <Icon icon={faMoon} className="text-indigo-400" />;
	}

	if (status === "running") {
		return (
			<Ping
				variant="success"
				{...props}
				className={cn("relative right-auto", props.className)}
			/>
		);
	}

	return (
		<span
			{...props}
			className={cn(
				"size-2 rounded-full",
				{
					"bg-blue-600 animate-pulse": status === "starting",
					"bg-destructive":
						status === "crashed" || status === "crash-loop",
					"bg-foreground/10": status === "stopped",
					"bg-primary": status === "pending",
					"bg-accent": status === "unknown",
				},
				props.className,
			)}
		/>
	);
};
